import { useEffect, useState } from "react";
import { api, ROLE_LABEL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MessageSquare } from "lucide-react";

export default function Discussions() {
  const [threads, setThreads] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/threads").then(r=>setThreads(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10">
      <div className="flex justify-between items-baseline flex-wrap gap-3">
        <div>
          <div className="label-mono text-[#d4b876]">COMMUNITY DISCOURSE</div>
          <h1 className="font-display text-4xl mt-2">Discussion threads</h1>
          <p className="text-white/60 mt-2 max-w-xl">Members-only, moderated, on-record. Unofficial — not a substitute for Council decisions.</p>
        </div>
        <button onClick={()=>setShowCreate(true)} className="btn-cta primary" data-testid="new-thread-btn"><Plus size={12}/> New thread</button>
      </div>

      <div className="mt-10 space-y-3">
        {threads.length === 0 && <div className="font-mono text-xs text-white/40">Be the first to open a thread.</div>}
        {threads.map(t => (
          <button key={t.id} onClick={()=>setOpenId(t.id)} className="w-full text-left panel p-5" data-testid={`thread-${t.id}`}>
            <div className="flex justify-between items-baseline gap-3">
              <div>
                <div className="font-mono text-[10px] text-[#89b4fa] tracking-widest uppercase">{t.category} · {ROLE_LABEL[t.created_by_role]}</div>
                <div className="font-display text-xl mt-1">{t.title}</div>
                <div className="text-white/60 text-sm mt-2 line-clamp-2">{t.body}</div>
              </div>
              <div className="font-mono text-[10px] text-white/40 flex items-center gap-1 shrink-0"><MessageSquare size={12}/> {t.reply_count}</div>
            </div>
            <div className="font-mono text-[10px] text-white/30 mt-3">{t.created_by_name} · {new Date(t.created_at).toLocaleString()}</div>
          </button>
        ))}
      </div>

      {showCreate && <CreateThread onClose={()=>setShowCreate(false)} onCreated={load} />}
      {openId && <ThreadDetail id={openId} onClose={()=>{ setOpenId(null); load(); }} />}
    </div>
  );
}

function CreateThread({ onClose, onCreated }) {
  const [f, setF] = useState({ title:"", body:"", category:"general" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/threads", f); toast.success("Thread opened"); onCreated(); onClose(); } catch(e){ toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg border border-white/20 bg-[#181825] p-6 space-y-4">
        <div className="label-mono text-[#d4b876]">NEW THREAD</div>
        <select value={f.category} onChange={e=>setF({...f, category:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="thread-category">
          <option value="general">General</option>
          <option value="housing">Housing</option>
          <option value="education">Education</option>
          <option value="health">Health</option>
          <option value="lands">Lands</option>
          <option value="culture">Culture</option>
        </select>
        <input required placeholder="Thread title" value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="thread-title" />
        <textarea required rows={6} placeholder="Body" value={f.body} onChange={e=>setF({...f, body:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="thread-body" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="submit-thread-btn">{saving?"Posting...":"Publish"}</button>
        </div>
      </form>
    </div>
  );
}

function ThreadDetail({ id, onClose }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => api.get(`/threads/${id}`).then(r=>setT(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    await api.post(`/threads/${id}/replies`, { body: reply });
    setReply(""); load();
  };
  if (!t) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto p-4 sm:p-8" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-3xl mx-auto border border-white/20 bg-[#181825] p-8">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="label-mono text-[#89b4fa]">{t.category} · {ROLE_LABEL[t.created_by_role]}</div>
            <h2 className="font-display text-3xl mt-1">{t.title}</h2>
            <div className="font-mono text-[10px] text-white/40 mt-2">{t.created_by_name} · {new Date(t.created_at).toLocaleString()}</div>
          </div>
          <button onClick={onClose} className="btn-cta" data-testid="close-thread">Close</button>
        </div>
        <div className="mt-5 text-white/80 whitespace-pre-wrap">{t.body}</div>
        <div className="divider-line my-8" />
        <div className="label-mono">Replies ({t.replies.length})</div>
        <div className="mt-4 space-y-3">
          {t.replies.map(r => (
            <div key={r.id} className="border border-white/10 p-4">
              <div className="font-mono text-[10px] text-[#d4b876] tracking-widest uppercase">{ROLE_LABEL[r.created_by_role]} · {r.created_by_name}</div>
              <div className="text-white/80 mt-2 whitespace-pre-wrap">{r.body}</div>
              <div className="font-mono text-[10px] text-white/30 mt-2">{new Date(r.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <textarea rows={3} placeholder="Contribute to the discussion..." value={reply} onChange={e=>setReply(e.target.value)} className="w-full px-3 py-2 font-mono text-sm" data-testid="reply-input" />
          <button type="submit" className="btn-cta primary" data-testid="submit-reply-btn">Post reply</button>
        </form>
      </div>
    </div>
  );
}
